import Container, { Column, Row } from "@/components/container/Container";
import { ButtonLabel, TextBodyExtraExtraSmall, TextBodyExtraSmall2, TextBodySmall, TextHeadlineMedium, TextLabelLarge2, TextLabelMedium2, TextLabelSmall2 } from "@/components/text/Texts";
import { Button } from "primereact/button";
import { useForm } from "react-hook-form";
import { MdAdd, MdArrowDropDown, MdArrowForward, MdClose, MdInfo, MdOutlineCancel, MdPerson, MdToday } from "react-icons/md";
import '../views/concierge.styles.css'
import { DropdownChangeEvent } from "primereact/dropdown";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showTypedModal, showTypedModal2 } from "@/common/common_utils";
import { SecondaryButtonMT } from "@/components/buttons/Buttons";
import { Tooltip } from "primereact/tooltip";
import { RadioButton } from "primereact/radiobutton";
import style from '@/modules/programs/views/program.module.css';
import moment, { Moment } from "moment";
import FlagsModel, { Flag } from "@/modules/authentication/sign-up/components/CountryFlags";
import { cn } from "@/components/ui/cn";
import { axiosInstance } from "@/services/axios.service";
import { ENDPOINTS } from "@/constants/endpoints";
import { v4 as uuidv4 } from 'uuid';
import { useMutation, useQuery } from "@tanstack/react-query";
import { SelectCountryIconDropdownConcierge } from "@/components/form-inputs/select-country/SelectCountryIconDropdown";
import ConciergeHeader from "../views/ConciergeHeader";
import { X4bLocation } from "../views/PlacesRepository";
import { ConciergeLiveMap } from "../map/ConciergeLiveMap";
import { useNavigate, useLocation } from "react-router-dom";
import { routeWithId } from "@/constants/routes";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { showNotification } from "@/modules/notification/engine/notification.library";
import _ from "lodash";
import TextAreaModal from "./TextAreaModal";
import AddressDropdownInput, { X4bPointType } from "./AddressDropdownInput";
import AddPassengerDialog from "./AddPassengerDialog";
import { VALIDATION_MESSAGES } from "@/constants/validation";
import { getNewRideVehicleTypeInfo, vehicleTypes, XpressVehicleType } from "@/modules/vehicle/engine/VehicleType";
import { GoogleMapsRoutingResponse } from "@/modules/map/engine/GoogleTypes";
import { DateHelper } from "@/common/DateHelper";
import { newRideDebugDefaults1 } from "@/common/DebugHelper";
import { BookingType, GetServicesAndPricesRequest, GetServicesAndPricesResponse, PassengerDto, PlaceOrderRequest, X4B_GET_SERVICES_NO_VEHICLES_MESSAGE, X4bActivityPointDto, XpressServiceType } from "@/modules/vehicle/engine/ActivityTypes";
import { string } from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";
import { PhoneHelper } from "@/common/PhoneHelper";
import DropdownInput from "@/components/inputs/DropdownInput";
import { InputController } from "@/components/inputs/InputController";
import TextInput from "@/components/inputs/TextInput";
import DateInput from "@/components/inputs/DateInput";

export interface NewRideType {
	firstName: string;
	lastName: string;
	phone: string;
	pickUpAddress: X4bLocation;
	dropOffAddress: X4bLocation;
	bookingType: BookingType;
	date?: Moment | null;
	timeInSeconds?: number | null;
	internalMemo?: string;
	noteToDriver?: string;
}
export interface ReturnTripType {
	initialData: Partial<NewRideType>,
	countryFlag?: Flag,
	vehicleType?: XpressVehicleType,
	additionalPassengers?: PassengerDto[]
}
interface TimeSlot {
	label: string;
	value: number;
}

const generateTimeSlots = (): TimeSlot[] => {
	const slots: TimeSlot[] = [];
	const now = moment();

	// Add "Now" as the first option
	// Error in figma design. Now is nonsense since we select time for day in future. slots.push({
	// 	label: "Now",
	// 	value: now.valueOf() // using timestamp as value
	// });

	// Round up to the next hour for the hourly slots
	const startTime = moment(now).startOf('day');

	// Generate 24 hourly slots
	for (let i = 0; i < 48; i++) {
		const slotTime = moment(startTime).add(i * 30, 'minutes');
		const secondsFromMidnight = slotTime.hours() * 3600 + slotTime.minutes() * 60;

		slots.push({
			label: slotTime.format('h:mm A'), // formats like "9:00 AM"
			value: secondsFromMidnight
		});
	}

	return slots;
};

const timeSlots = generateTimeSlots();

const ConciergeNewRide = () => {
	const formId = 'newRideForm';
	const location = useLocation();

	// Try to get return trip from location.state first, then fall back to sessionStorage
	const getReturnTripData = (): ReturnTripType | undefined => {
		// First try: location.state (standard React Router way)
		if (location.state?.returnTrip) {
			const returnTrip = location.state.returnTrip as ReturnTripType;


			// Validate that critical properties exist (protection against obfuscation)
			if (!returnTrip.initialData) {
				console.error('[ConciergeNewRide] location.state.returnTrip is missing initialData - possible obfuscation issue');
				// Fall through to sessionStorage
			} else {
				// Convert ISO string back to Moment object
				if (returnTrip.initialData.date && typeof returnTrip.initialData.date === 'string') {
					returnTrip.initialData.date = moment(returnTrip.initialData.date);
					// I think we don't need it for return trip
					//returnTrip.initialData.timeInSeconds = returnTrip.initialData.date.diff(returnTrip.initialData.date.clone().startOf('day'), 'seconds')
				}
				return returnTrip;
			}
		}

		// Second try: sessionStorage fallback (for cases where location.state is lost or obfuscated)
		try {
			const stored = sessionStorage.getItem('pendingReturnTrip');
			if (stored) {
				const parsed = JSON.parse(stored) as ReturnTripType;


				// Validate structure
				if (!parsed.initialData) {
					console.error('[ConciergeNewRide] sessionStorage data is missing initialData structure');
					sessionStorage.removeItem('pendingReturnTrip');
					return undefined;
				}

				// Convert ISO string back to Moment object
				if (parsed.initialData?.date && typeof parsed.initialData.date === 'string') {
					parsed.initialData.date = moment(parsed.initialData.date);
				}

				// Clear it after retrieving to avoid reusing stale data
				sessionStorage.removeItem('pendingReturnTrip');
				return parsed;
			}
		} catch (error) {
			console.error('[ConciergeNewRide] Failed to parse return trip from sessionStorage:', error);
		}

		return undefined;
	};

	const locationState = getReturnTripData();
	const initialData = locationState?.initialData;
	const initialCountryFlag = locationState?.countryFlag;
	const initialAdditionalPassengers = locationState?.additionalPassengers;


	const [selectedCountry, setSelectedCountry] = useState<Flag | undefined>(
		initialCountryFlag || FlagsModel.flagByCountryCode('PH')
	);
	const [orderUuid, setOrderUuid] = useState<string>("");
	const [googleMapsRoute, setGoogleMapsRoute] = useState<GoogleMapsRoutingResponse | null>(null);

	const [additionalPassengers, setAdditionalPassengers] = useState<PassengerDto[]>(initialAdditionalPassengers || []);

	// Get default values - only use debug defaults if NO return trip data exists
	// This prevents debug defaults (especially date/timeInSeconds) from interfering with return trip data
	const getDefaultValues = () => {
		if (initialData) {

			return initialData;
		} else {
			// No return trip - use debug defaults
			const debugDefaults = newRideDebugDefaults1() as Partial<NewRideType>;

			return debugDefaults;
		}
	};

	const form = useForm<NewRideType>({
		defaultValues: getDefaultValues(),
		resolver: yupResolver(yup.object({
			firstName: yup.string().trim().required(VALIDATION_MESSAGES.required),
			lastName: yup.string().trim().required(VALIDATION_MESSAGES.required),
			phone: string().trim().required(VALIDATION_MESSAGES.required)
				.test('is-valid-phone', 'Please enter a valid phone number', function (value) {
					if (!value) return true; // Let required validation handle empty values
					try {
						const countryCode = this.parent.mobileNumberPrefix || 'PH';
						const prepared = PhoneHelper.preparePhoneNumber(value, countryCode);
						const res = isValidPhoneNumber(prepared, countryCode);
						return res;
					} catch (error) {
						// If validation fails, return false
						return false;
					}
				}),
			pickUpAddress: yup.mixed<X4bLocation>().required(VALIDATION_MESSAGES.required),
			dropOffAddress: yup.mixed<X4bLocation>().required(VALIDATION_MESSAGES.required),
			bookingType: yup
				.mixed<BookingType>()
				.oneOf([BookingType.Asap, BookingType.Scheduled, BookingType.Flexible])
				.required(VALIDATION_MESSAGES.required),
			date: yup.mixed<Moment>().nullable().when('bookingType', {
				is: BookingType.Scheduled,
				then: (schema) => schema.required(VALIDATION_MESSAGES.required),
				otherwise: (schema) => schema.optional().nullable(),
			}),
			timeInSeconds: yup.number().nullable().when('bookingType', {
				is: BookingType.Scheduled,
				then: (schema) => schema.required(VALIDATION_MESSAGES.required)
					.min(0, 'Invalid time')
					.max(86400, 'Invalid time')
					.test('not-past-time', VALIDATION_MESSAGES.past_schedule,
						function (value) {
							if (value === undefined || value === null) return false;

							const date = this.parent.date as Moment;
							if (!date) return true;

							const scheduledDateTime = date.startOf('day').add(value, 'seconds');
							return scheduledDateTime.isAfter(moment().subtract(1, 'minute'));
						}
					),
				otherwise: (schema) => schema.optional().nullable(),
			}),
			internalMemo: yup.string().trim().transform(value => value || undefined).optional(),
			noteToDriver: yup.string().trim().transform(value => value || undefined).optional(),
		})),
		mode: 'all',// Don't use 'onBlur' here. It works badly with conditional validation (.when()). As result for Scheduled ride the 'Request Now' button stays disabled even when all required fields are filled.
	});

	// Trigger validation when initial data is provided (e.g., from return trip)
	useEffect(() => {
		if (initialData) {
			// Trigger validation for all fields to enable the submit button
			form.trigger();
		}
	}, [initialData, form]);

	useEffect(() => {
		const subscription = form.watch((value, { name, type }) => {
			if (type !== 'change') return;

			// Clear date and time fields when switching to ASAP or Flexible
			if (name === 'bookingType' && (value.bookingType === BookingType.Asap || value.bookingType === BookingType.Flexible)) {
				form.setValue('date', undefined, { shouldValidate: false });
				form.setValue('timeInSeconds', undefined, { shouldValidate: false });
				setTimeout(() => form.trigger(['date', 'timeInSeconds']), 0);
			}

			// Re-trigger timeInSeconds validation when date changes for Scheduled bookings
			if (value.bookingType === BookingType.Scheduled && name === 'date' && value.timeInSeconds !== undefined) {
				setTimeout(() => form.trigger('timeInSeconds'), 0);
			}
		});

		return () => subscription.unsubscribe();
	}, [form]);

	const values = form.watch();

	const routeGeoReady = useMemo(
		() =>
			!!(
				values.pickUpAddress?.Latitude &&
				values.pickUpAddress?.Longitude &&
				values.dropOffAddress?.Latitude &&
				values.dropOffAddress?.Longitude
			),
		[
			values.pickUpAddress?.Latitude,
			values.pickUpAddress?.Longitude,
			values.dropOffAddress?.Latitude,
			values.dropOffAddress?.Longitude,
		],
	);

	// Helper function to construct points for API requests
	const buildPointsForRequest = useCallback(() => {
		return [values.pickUpAddress, values.dropOffAddress]
			.filter(i => i?.Latitude && i?.Longitude)
			.map((point, ind) => ({
				PlaceName: point?.PlaceName,
				Address: point?.Address,
				Latitude: point?.Latitude,
				Longitude: point?.Longitude,
				SortOrder: ind,
				Name: point?.Name
			}) as X4bActivityPointDto);
	}, [values.pickUpAddress, values.dropOffAddress]);

	// Setting trip id in the LiveMap component is tricky because the component itself is momoized. So, we can pass trip id in normal way and need to use bind setter and use refs.
	const tripIdSetterRef = useRef<(tripId: string) => void>(undefined);
	const bindTripIdSetter = useCallback((setter: (tripId: string) => void) => {
		tripIdSetterRef.current = setter;
	}, []);

	const orderUuidSetterRef = useRef<(orderUuid: string) => void>(undefined);
	const bindOrderUuidSetter = useCallback((setter: (tripId: string) => void) => {
		orderUuidSetterRef.current = setter;
	}, []);

	const servicesQ = useQuery({
		queryKey: ['NewRide.ServicesAndPrices', XpressServiceType.Ride, values.pickUpAddress, values.dropOffAddress],
		queryFn: async () => {
			const pointsForRequest = buildPointsForRequest();


			if (pointsForRequest.length < 2) {
				console.error('[ConciergeNewRide] Insufficient points for GetServicesAndPrices', {
					pickUpAddress: values.pickUpAddress,
					dropOffAddress: values.dropOffAddress,
					hasPickUpLatLng: !!(values.pickUpAddress?.Latitude && values.pickUpAddress?.Longitude),
					hasDropOffLatLng: !!(values.dropOffAddress?.Latitude && values.dropOffAddress?.Longitude)
				});
				throw new Error('Both pickup and dropoff addresses are required');
			}

			const result = await axiosInstance.post<GetServicesAndPricesResponse>(ENDPOINTS['/api/X4bActivity/GetServicesAndPrices'], {
				Points: pointsForRequest,
				ServiceTypeId: XpressServiceType.Ride,
			} satisfies GetServicesAndPricesRequest);

			if (!(result.data.VehicleTypes?.length)) {
				throw new Error(X4B_GET_SERVICES_NO_VEHICLES_MESSAGE);
			}

			// Parse GoogleMapsRouting JSON string if it's provided
			if (result.data.GoogleMapsRouting) {
				try {
					const parsedRoute = JSON.parse(result.data.GoogleMapsRouting) as GoogleMapsRoutingResponse;
					setGoogleMapsRoute(parsedRoute);
				} catch (error) {
					console.error('Failed to parse GoogleMapsRouting:', error);
					setGoogleMapsRoute(null);
				}
			} else {
				setGoogleMapsRoute(null);
			}

			return result.data;
		},
		enabled: routeGeoReady,
		refetchOnWindowFocus: false
	});

	const placeOrderMutation = useMutation({
		mutationFn: async () => {
			if (!servicesQ.data) throw new Error('Services data not available');

			const xpressId = uuidv4();

			const pointsForRequest = buildPointsForRequest();
			if (pointsForRequest.length < 2) {
				throw new Error('Both pickup and dropoff addresses are required');
			}

			const xpressService = servicesQ.data.VehicleTypes.find(i => i.VehicleTypeId == selectedVehicle.xpressVehicleType);
			if (!xpressService) throw new Error('Unexpected error. Service does not match.');

			const scheduledAt = moment(values.date).startOf('day').add(values.timeInSeconds, 'seconds').toDate();
			const scheduledAtS = values.bookingType === BookingType.Scheduled ? DateHelper.stripTimezoneFromIso8601(scheduledAt) : moment().toDate();


			const placeOrder = await axiosInstance.post<any>(ENDPOINTS['/api/X4bActivity/PlaceOrder'], {
				XpressId: xpressId,
				BookingType: values.bookingType,
				FareId: xpressService.FareId,
				Amount: xpressService.Amount,
				VehicleType: xpressService.VehicleTypeId,
				Memo: values.internalMemo,
				Note: values.noteToDriver,
				ScheduledAt: scheduledAtS,
				TimeZoneOffset: moment().utcOffset() / 60,
				Points: pointsForRequest,
				HeadPassenger: {
					FirstName: values.firstName,
					LastName: values.lastName,
					Phone: PhoneHelper.preparePhoneNumber(values.phone, selectedCountry?.code || 'PH')
				},
				AdditionalPassengers: additionalPassengers,
				GoogleMapsRouting: servicesQ.data.GoogleMapsRouting,
				ServiceTypeId: XpressServiceType.Ride,
			} as PlaceOrderRequest);

			return { xpressId, placeOrder };
		},
		onSuccess: ({ xpressId }) => {
			setOrderUuid(xpressId);
			orderUuidSetterRef.current?.(xpressId);
			showNotification('Booking has been requested');
			navigate('/concierge/statuses/0');
		},
		// I don't know why it was added, but our current generic error handler understands error messages much better, so, let's keep it for now:
		//  onError: (error) => {
		// 	showError(error.message);
		// }
	});


	const scheduleTypeBlock = (type: BookingType, title: string, description: string) => (
		<Row
			className={cn(
				'gap-3 justify-between items-center bg-surfaceContainerLow rounded-[20px] px-6 py-4',
				values.bookingType === type && 'shadow-[inset_0_0_0_2px_theme(colors.tertiary)]'
			)}
			onClick={() => form.setValue("bookingType", type, { shouldValidate: true })}
		>
			<Column className="gap-3">
				<TextLabelLarge2 text={title} className="text-primary" />
				<TextBodySmall text={description} className="text-primary" />
			</Column>
			<RadioButton
				inputId={`booking-${type}`}
				value={type}
				onChange={(e) => form.setValue('bookingType', e.value, { shouldValidate: true })}
				checked={values.bookingType === type}
				pt={{
					box: { className: style['program-radio-input'] },
					icon: { className: style['program-radio-icon'] },
				}}
			/>
		</Row>
	);

	const detailsBtn = (id: 'internalMemo' | 'noteToDriver', label: string) => {
		const value = form.getValues(id);
		const hasValue = !!value;

		return (
			<Button
				className={cn(
					'flex flex-row gap-2 items-center h-10 rounded-[100px]',
					'self-start relative z-[1] transition duration-300',
					hasValue ? 'pl-6 pr-4 bg-primary' : 'pl-4 pr-6 bg-secondaryContainer',
				)}
				onClick={async () => {
					const result = await showTypedModal(TextAreaModal, { title: 'Add ' + label, label: label, value: value });
					if (!result) return;
					form.setValue(id, result);

					showNotification(
						_.capitalize(label.toLowerCase()) + ' added',
						{ closable: true, closeIcon: (<MdClose size={24} className="text-neutral96" />) },
						undefined,
						{ pt: { root: { className: 'w-[284px] !bottom-2 !left-6' }, content: { className: 'h-12 pl-4 pr-3 !py-0' } } }
					);
				}}
			>
				{!hasValue && (<MdAdd className="text-secondary" size={18} />)}

				<TextLabelLarge2
					text={label}
					className={cn(
						'transition duration-300',
						hasValue ? 'text-white' : 'text-secondary',
					)}
				/>

				{hasValue && (
					<MdOutlineCancel
						size={18 + 8}
						className="p-1 text-white relative z-[2]"
						onClick={(e) => {
							form.setValue(id, undefined);
							e.stopPropagation();
						}}
					/>
				)}
			</Button>
		);
	}

	// Store only the vehicle type ID, derive the full vehicle object with price from API data
	const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState<XpressVehicleType>(locationState?.vehicleType ?? vehicleTypes[1].xpressVehicleType);
	const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
	const isAddPassengersDisabled = additionalPassengers.length >= 3;
	const navigate = useNavigate();

	// Automatically derive selectedVehicle with correct price from API data
	const selectedVehicle = useMemo(() => {
		if (servicesQ.data?.VehicleTypes && servicesQ.data.VehicleTypes.length > 0) {
			// Try to find the selected vehicle type in the API response
			const matchingVehicleType = servicesQ.data.VehicleTypes.find(
				vt => vt.VehicleTypeId === selectedVehicleTypeId
			);

			if (matchingVehicleType) {
				// Return vehicle with correct price from API
				return getNewRideVehicleTypeInfo(matchingVehicleType.VehicleTypeId, matchingVehicleType.Amount);
			} else {
				// If selected type doesn't exist in API response, use first available
				const firstVehicleType = servicesQ.data.VehicleTypes[0];
				return getNewRideVehicleTypeInfo(firstVehicleType.VehicleTypeId, firstVehicleType.Amount);
			}
		}
		// Fallback to default vehicle when API data is not available
		return vehicleTypes.find(vt => vt.xpressVehicleType === selectedVehicleTypeId) || vehicleTypes[1];
	}, [servicesQ.data, selectedVehicleTypeId]);

	// Update selectedVehicleTypeId if current selection doesn't exist in API response
	useEffect(() => {
		if (servicesQ.data?.VehicleTypes && servicesQ.data.VehicleTypes.length > 0) {
			const matchingVehicleType = servicesQ.data.VehicleTypes.find(
				vt => vt.VehicleTypeId === selectedVehicleTypeId
			);
			if (!matchingVehicleType) {
				// Current selection doesn't exist in API response, select first available
				setSelectedVehicleTypeId(servicesQ.data.VehicleTypes[0].VehicleTypeId);
			}
		}
	}, [servicesQ.data, selectedVehicleTypeId]);

	return (
		<Column className="w-full h-screen">
			<ConciergeHeader marginClass="mx-6 mt-4" />

			<Row className="x4b-concierge w-full h-[calc(100%-88px)] p-6 gap-6"> {/* 88px - header */}
				<Column className="h-full w-[412px] pb-6 rounded-[26px] bg-white blue-scroll">
					<Row className="p-6 items-center justify-between border-b border-[#CCDFE5]">
						<TextHeadlineMedium text="New Ride" className="text-primary" />
						<button
							onClick={() => navigate(routeWithId('/concierge/statuses/:id', 0))}
							className="rounded-full p-1 transition duration-200 hover:bg-gray-200"
							children={(<MdClose size={24} className="text-black" />)}
						/>
					</Row>

					<Column className="gap-10 pl-6 pr-4 mr-2 my-3 pt-3 pb-6 h-full overflow-y-auto blue-scroll">
						{/* Top */}
						<Column className="gap-5">
							{/* <Row className="gap-2">
								<Button
									id={'newRideOneWayButton'}
									data-testid={'newRideOneWayButtonTestId'}
									className="w-1/2 next-button"
									// onClick={onClick}
									// disabled={disabled}
									type='button'
								>
									{<MdArrowForward size={18} />}
									<ButtonLabel text='One Way' />
								</Button>
								<Button
									id={'newRideRoundTripButton'}
									data-testid={'newRideRoundTripButtonTestId'}
									className="w-1/2 next-button"
									// onClick={onClick}
									disabled={true}
									type='button'
								>
									{<MdArrowForward size={18} />}
									<ButtonLabel text='Round Trip' />
								</Button>
							</Row> */}

							<Column className="gap-3">
								<TextLabelLarge2 className="text-primary font-semibold" text="Who’s Riding?" />
								<Row className="gap-2">
									<InputController id="firstName" control={form.control}>
										<TextInput variant="outlined" label="First Name" />
									</InputController>
									<InputController id="lastName" control={form.control}>
										<TextInput variant="outlined" label="Last Name" />
									</InputController>
								</Row>
							</Column>

							<Column className="gap-3">
								<TextLabelLarge2 className="text-primary font-semibold" text="Telephone" />
								<Row className="gap-2">
									<SelectCountryIconDropdownConcierge
										value={selectedCountry}
										onChange={(e: DropdownChangeEvent) => {
											form.resetField('phone');
											setSelectedCountry(e.value)
										}}
									/>
									<InputController id="phone" control={form.control}>
										<TextInput variant="outlined" label="Phone Number" keyfilter="num" />
									</InputController>
								</Row>
							</Column>

							<div className="bg-neutralVariant30 h-[1px] shrink-0" />

							{additionalPassengers.length > 0 && (
								<Row className="flex-wrap gap-3">
									{additionalPassengers.map((passenger, index) => (
										<Row key={index} className="h-8 px-2 gap-2 items-center border border-primary rounded-[8px]">
											<MdPerson size={18} className="text-primary" />
											<TextLabelLarge2
												className="text-onSurfaceVariant"
												text={`${passenger.FirstName} ${passenger.LastName}`}
											/>
											<MdClose
												size={18}
												className="text-onSurfaceVariant cursor-pointer"
												onClick={() => setAdditionalPassengers(prev => prev.filter((_, i) => i !== index))}
											/>
										</Row>
									))}
								</Row>
							)}

							<Row className="gap-3 items-center">
								<SecondaryButtonMT
									id="add-people"
									label="Add Passenger"
									// Since we do manual styling for icon here, the disabled state also needs manual styling.
									icon={() => <MdAdd className={cn('rounded-full', isAddPassengersDisabled ? " bg-[#8B8B8B] text-secondaryContainer" : "bg-primary text-white")} />}
									className="secondary with-icon-button"
									disabled={isAddPassengersDisabled}
									onClick={async () => {
										const res = await showTypedModal2<{}, PassengerDto>(AddPassengerDialog, {});
										if (res) setAdditionalPassengers(prev => [...prev, res]);
									}}
								/>
								<Tooltip
									target="#auto-approve-info"
									position="top"
									className="custom-tooltip"
									content="You can add up to 4 passengers. Available vehicles may vary."
								/>
								<MdInfo
									id="auto-approve-info"
									size={24}
									className="cursor-pointer text-primary"
								/>
							</Row>
						</Column>

						{/* Ride Details */}
						<Column className="gap-6">
							<Column className="gap-5">
								<TextLabelLarge2 className="text-primary font-semibold" text="Where are they Going?" />
								<AddressDropdownInput
									id="pickUpAddress"
									label="Pick Up Address"
									formName={formId}
									control={form.control}
									pointType={X4bPointType.Pickup}
								/>
								<AddressDropdownInput
									id="dropOffAddress"
									label="Drop Off Address"
									formName={formId}
									control={form.control}
									pointType={X4bPointType.Dropoff}
								/>
							</Column>

							<Column className="gap-4">
								<TextLabelLarge2 className="text-primary font-semibold" text="Choose how to request the ride" />
								{scheduleTypeBlock(BookingType.Scheduled, 'Scheduled', 'You set the pickup time for the ride')}
								{scheduleTypeBlock(BookingType.Asap, 'ASAP', 'A ride is requested for immediate pickup.')}
								{scheduleTypeBlock(BookingType.Flexible, 'Flexible', 'Passenger request the ride via SMS when they’re ready')}
								<Row className="gap-[10px]">
									<Tooltip
										target="#schedule-type-info"
										position="top"
										className="custom-tooltip"
										content="Flexible rides are better for reliability."
									/>
									<MdInfo
										id="schedule-type-info"
										size={24}
										className="cursor-pointer text-primary"
									/>

									<TextBodySmall className="text-primary" text="Flexible rides are better for reliability." />
								</Row>
							</Column>

							{values.bookingType === BookingType.Scheduled && (
								<Column className="gap-4">
									<TextLabelLarge2 className="text-primary font-semibold" text="Schedule Ride Booking" />
									<InputController id="date" control={form.control}>
										<DateInput
											variant="outlined"
											label="Date"
											icon={(<MdToday size={24} className="text-black" />)}
											iconCls="pointer-events-none"
											calendarProps={{
												showClearBtn: false,
												minDate: moment().startOf('day'),
												maxDate: moment().add(1, 'year').endOf('day'),
											}}
										/>
									</InputController>
									<InputController id="timeInSeconds" control={form.control}>
										<DropdownInput
											variant="outlined"
											label="Time"
											options={timeSlots}
										/>
									</InputController>
								</Column>
							)}

							<Column className="gap-4">
								<TextLabelLarge2 className="text-primary font-semibold" text="Select vehicle type" />
								{!routeGeoReady && (
									<Row className="h-[102px] items-center bg-surfaceContainer p-4 rounded-[28px] text-center opacity-60">
										<TextBodySmall className="text-primary" text="Select pickup and drop-off addresses to see available vehicles" />
									</Row>
								)}
								{routeGeoReady && servicesQ.isFetching && (
									<Row className="h-[102px] items-center bg-surfaceContainer p-4 rounded-[28px] justify-center">
										<i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }} />
										<span className="ml-3 text-primary">Loading available vehicles...</span>
									</Row>
								)}
								{routeGeoReady && !servicesQ.isFetching && servicesQ.isError && (
									<Row className="min-h-[102px] items-center bg-surfaceContainer p-4 rounded-[28px]">
										<TextBodySmall
											className="text-error60"
											text={servicesQ.error instanceof Error ? servicesQ.error.message : X4B_GET_SERVICES_NO_VEHICLES_MESSAGE}
										/>
									</Row>
								)}
								{routeGeoReady && !servicesQ.isFetching && !servicesQ.isError && !!servicesQ.data?.VehicleTypes?.length && (
									<div style={{ position: 'relative' }}>
										{/* Dropdown trigger row - use div instead of Row for onClick */}
										<Row
											className={cn(
												'bg-surfaceContainer p-4 gap-3 cursor-pointer items-center',
												vehicleDropdownOpen ? 'rounded-t-[28px]' : 'rounded-[28px]'
											)}
											onClick={() => setVehicleDropdownOpen((open) => !open)}
										>
											<Column className="w-[70px] h-[70px] relative">
												<img
													className="absolute left-1/2 -translate-x-1/2 -top-2"
													src={selectedVehicle.serviceImagePath}
													width={58} height={58} alt=""
												/>
												<Row className="absolute left-1/2 -translate-x-1/2 bottom-2">
													<MdPerson className="text-primary" size={20} />
													<TextLabelMedium2 className="text-primary" text={selectedVehicle.passengersNumber.toString()} />
												</Row>
											</Column>
											<Column className="gap-3">
												<Row className="justify-between">
													<TextLabelLarge2 className="text-primary" text={selectedVehicle.serviceShortTitle} />
													<TextLabelLarge2 className="text-primary" text={`₱${(selectedVehicle.price ?? 0).toFixed(2)}`} />
												</Row>
												<TextBodyExtraSmall2 className="text-primary" text={selectedVehicle.serviceDescription} />
											</Column>
											<MdArrowDropDown size={24} className="text-onSurfaceVariant" />
										</Row>
										{vehicleDropdownOpen && (
											<Column className="absolute left-0 right-0 z-10">
												{servicesQ.data.VehicleTypes.map((vehicleType, index) => {
													const vehicle = getNewRideVehicleTypeInfo(vehicleType.VehicleTypeId, vehicleType.Amount);
													const isSelected = vehicle.xpressVehicleType === selectedVehicle.xpressVehicleType;
													const isLast = index === servicesQ.data.VehicleTypes.length - 1;

													return (
														<Row
															key={vehicle.serviceTitle}
															className={cn(
																'h-16 gap-4 pl-4 pr-6 items-center cursor-pointer', isLast && 'rounded-b-[28px]',
																'bg-surface', !isSelected && 'hover:bg-[#DFE3E6]', isSelected && 'bg-[#D1D4D6]'
															)}
															onClick={() => {
																setSelectedVehicleTypeId(vehicle.xpressVehicleType);
																setVehicleDropdownOpen(false);
															}}
														>
															<img src={vehicle.serviceImagePath} width={40} height={40} alt="" />
															<Column className="flex-1">
																<TextBodySmall className="text-onSurface" text={`${vehicle.serviceTitle} 👤${vehicle.passengersNumber}`} />
																<TextBodyExtraExtraSmall className="text-onSurfaceVariant" text={vehicle.serviceDescription} />
															</Column>
															<TextLabelSmall2 className="text-onSurfaceVariant" text={`₱${(vehicle.price ?? 0).toFixed(2)}`} />
														</Row>
													);
												})}
											</Column>
										)}
									</div>
								)}

								{detailsBtn('internalMemo', 'Internal Memo')}
								{detailsBtn('noteToDriver', 'Note to Driver')}
							</Column>
						</Column>
					</Column>

					<Row className="h-20 px-6 py-3 gap-2 justify-end border-t border-[#CCDFE5]">
						{/* it is actually RoundButton with couple styles, but hard to understand which ones. See People page, Approve button*/}
						{/* <Button
							id="route-button"
							label="Start Routing"
							onClick={onStartRouting}
							pt={{
								root: { className: 'h-8 px-2 rounded-[100px] bg-primary60' },
								label: { className: 'font-poppins text-label-small text-white' },
							}}
						/> */}
						<Button
							id="request-ride-button"
							label={placeOrderMutation.isPending ? "Requesting..." : (!orderUuid ? "Request Now" : "Requested")}
							loading={placeOrderMutation.isPending}
							disabled={
								!form.formState.isValid ||
								placeOrderMutation.isPending ||
								!!orderUuid ||
								(routeGeoReady &&
									(servicesQ.isPending ||
										servicesQ.isError ||
										!servicesQ.data?.VehicleTypes?.length))
							}
							onClick={form.handleSubmit((data) => {
								placeOrderMutation.mutate();
							})}
							pt={{
								root: { className: 'px-[36.5px] py-4 rounded-[100px] bg-primary60' },
								label: { className: 'font-poppins text-label-large2 text-white' },
							}}
						/>
					</Row>
				</Column>

				<Container className="overflow-hidden h-full grow rounded-[28px]">
					{/* We can't allow live map to redraw. I used memo for this for now, but not sure if it is fine and work well when we add functionality.*/}
					<ConciergeLiveMap
						bindTripIdSetter={bindTripIdSetter}
						bindOrderUuidSetter={bindOrderUuidSetter}
						pickupLocationName={values.pickUpAddress?.PlaceName || values.pickUpAddress?.Address}
						initialRoute={googleMapsRoute}
						activity={undefined}
					// selectGeoZone={{ PolygonGeometries: [{ Lat: 14.6350728, Lng: 120.9927488 }] }}
					// onMapBoundsChanged={(bounds) => console.log('Map bounds changed:', bounds)}}
					/>
				</Container>
			</Row>
		</Column>
	);
};

export default ConciergeNewRide;


